import React, { useRef, useEffect, useState, forwardRef, memo } from "react";
import styled from "@emotion/styled";
import { useGetResizer } from "../../../contexts/Resizer";

import { useViewportScroll, motion, useTransform } from "framer-motion";

import { spacing, screen } from "../../../styles/mixins";
import {
	fontRoslindaleDisplay,
	h5,
	richText,
} from "../../../styles/typography";
import { StaticImage } from "gatsby-plugin-image";
import pillarFrontImage from "../../../images/pillar-front.png";
import { css } from "@emotion/react";
import { br } from "../../../styles/settings";

const ratioPillarTop = 0.479;

const Visualisation = styled.div`
	${spacing("marginTop", "l")};
	border-color: 3px solid #ccc;
	margin-bottom: -7.5%;
`;

const Screen = styled.div`
	position: sticky;
	height: 100vh;
	top: 0;
	overflow: hidden;
`;

const ScrollContainer = styled(motion.div)`
	position: absolute;
	top: 0;
	right: 0;
	display: grid;
	grid-auto-columns: calc(40 * 100vw / 48);
	grid-auto-flow: column;
	align-items: end;
	margin-left: calc(4 * 100vw / 48);
	margin-right: calc(4 * 100vw / 48);
	will-change: transform;

	@media ${screen("md")} {
		grid-auto-columns: calc(34 * 100vw / 48);
		margin-left: calc(7 * 100vw / 48);
		margin-right: calc(7 * 100vw / 48);
	}

	@media ${screen("ml")} {
		grid-auto-columns: calc(24 * 100vw / 48);
		margin-left: calc(12 * 100vw / 48);
		margin-right: calc(12 * 100vw / 48);
	}

	@media ${screen("mx")} {
		grid-auto-columns: calc(20 * 100vw / 48);
		margin-left: calc(14 * 100vw / 48);
		margin-right: calc(14 * 100vw / 48);
	}
`;

const Section = styled.div`
	position: relative;
	display: grid;
	grid-template-columns: 1fr;
	grid-template-rows: 1fr max-content;
	grid-template-areas: "content" "block";
	align-content: end;
`;

const Content = styled.div`
	width: 90%;
	color: #000000;
	grid-area: content;
	${spacing("paddingBottom", "l")};
	display: flex;
	flex-direction: column;
	justify-content: flex-end;
	margin-left: auto;
	margin-right: auto;
	position: relative;
	z-index: 2;

	@media ${screen("md")} {
		width: 80%;
	}

	@media ${screen("lg")} {
		width: 70%;
	}
`;

const Number = styled.p`
	${spacing("marginBottom", "xxs")};

	span {
		${fontRoslindaleDisplay};
		font-style: italic;
	}
`;

const Title = styled.h2`
	${h5};
	${spacing("marginBottom", "m")};
	margin-top: 0;
`;

const RichText = styled.div`
	${richText};
`;

const Block = styled.div`
	width: 100%;
	height: ${(props) => props.height ?? "100vh"};
	grid-area: block;
	display: flex;
	flex-direction: column;
	position: relative;

	img {
		opacity: 1 !important;
	}
`;

const PillarMan = styled.div`
	position: absolute;
	top: -40%;
	left: -5%;
	z-index: 10;
	width: 110%;
	height: 100%;
`;

const PillarTop = styled.div`
	padding-bottom: calc(1050 / 2184 * ${ratioPillarTop} * 100%);
	position: relative;
	z-index: 5;
`;

const pillarTopImage = css`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
`;

const PillarFront = styled.div`
	background-image: ${`url(${pillarFrontImage})`};
	background-repeat: repeat-y;
	background-size: 100% auto;
	background-position: top center;
	flex-grow: 1;
`;

const PillarBottom = styled.div`
	width: calc(3516 / 2184 * 100%); // bottom width / front width * 100%
	margin-top: -1px;
`;

const Ground = styled.div`
	width: 100vw;
	padding-bottom: 46vw;
	position: absolute;
	bottom: 40vw;
	right: -5vw;

	@media ${screen("md")} {
		bottom: 32vw;
		right: -8vw;
	}

	@media ${screen("ml")} {
		padding-bottom: 33vw;
		width: 75vw;
		bottom: 20.6vw;
		right: -12.5vw;
	}

	@media ${screen("mx")} {
		bottom: 17vw;
	}
`;

const povertyLine3Image = css`
	position: absolute;
	top: -2%;
	right: 20%;
	width: 60%;

	@media ${screen("ml")} {
		opacity: 0.3;
		width: 50%;
		right: 30%;
		left: auto;
		top: 4%;
	}
`;

const povertyLine2Image = css`
	position: absolute;
	width: 100%;
	top: 37%;
	left: -5%;
	transform: translateY(-50%);

	@media ${screen("ml")} {
		opacity: 0.8;
		width: 88%;
		top: 38%;
		left: -5%;
	}
`;

const povertyLine1Image = css`
	position: absolute;
	bottom: 16%;
	right: 30%;
	width: 69%;
`;

const povertyManImage = css`
	position: absolute;
	bottom: -8%;
	right: -14.5%;
	width: 29%;
	z-index: 1;

	@media ${screen("md")} {
		bottom: -10%;
	}

	@media ${screen("ml")} {
		bottom: -16%;
		right: -14.5%;
	}

	@media ${screen("mx")} {
		bottom: -19.8%;
		right: -14%;
	}
`;

const SocialClasses = memo(
	forwardRef(function ({ refTop, refBottom, classes }, ref) {
		const [windowHeight, setWindowHeight] = useState(768);
		const [windowWidth, setWindowWidth] = useState(1024);
		const [colWidth, setColWidth] = useState(24 / 48);
		const [marginTop, setMarginTop] = useState(0.5);
		const [blockHeight, setBlockHeight] = useState(3);

		const endSequenceHeight = 0;
		const height = blockHeight + endSequenceHeight;

		const images = [
			<StaticImage
				src={`../../../images/class-1.png`}
				alt="Person"
				placeholder="none"
				layout="fullWidth"
				sizes="(min-width: 1024px) 47vw, (min-width: 768px) 79vw, 93vw"
				loading="eager"
			/>,
			<StaticImage
				src={`../../../images/class-2.png`}
				alt="Person"
				placeholder="none"
				layout="fullWidth"
				sizes="(min-width: 1024px) 47vw, (min-width: 768px) 79vw, 93vw"
				loading="eager"
			/>,
			<StaticImage
				src={`../../../images/class-3.png`}
				alt="Person"
				placeholder="none"
				layout="fullWidth"
				sizes="(min-width: 1024px) 47vw, (min-width: 768px) 79vw, 93vw"
				loading="eager"
			/>,
			<StaticImage
				src={`../../../images/class-4.png`}
				alt="Person"
				placeholder="none"
				layout="fullWidth"
				sizes="(min-width: 1024px) 47vw, (min-width: 768px) 79vw, 93vw"
				loading="eager"
			/>,
			<StaticImage
				src={`../../../images/class-5.png`}
				alt="Person"
				placeholder="none"
				layout="fullWidth"
				sizes="(min-width: 1024px) 47vw, (min-width: 768px) 79vw, 93vw"
				loading="eager"
			/>,
		];

		const data = classes.map(({ title, content }, i) => {
			return {
				title,
				text: content,
				value:
					((classes.length - i) * blockHeight) / classes.length +
					endSequenceHeight,
				imageEl:
					i !== classes.length - 1 ? <PillarMan>{images[i]}</PillarMan> : null,
			};
		});

		const { scrollY } = useViewportScroll();

		// progressX is linear
		const progressX = useTransform(
			scrollY,
			[
				refTop,
				refBottom -
					data[data.length - 1].value * windowWidth +
					(1 - marginTop) * windowHeight,
			],
			[0, (data.length - 1) * colWidth * windowWidth]
		);

		// progressY is linear
		const progressY = useTransform(
			scrollY,
			[refTop, refBottom],
			[0, -1 * (refBottom - refTop) - marginTop * windowHeight]
		);

		useEffect(() => {
			const handleResize = () => {
				updateWindowHeight();
				updateWindowWidth();
			};

			const updateWindowHeight = () => {
				setWindowHeight(window.innerHeight);
			};

			const updateWindowWidth = () => {
				setWindowWidth(window.innerWidth);

				if (window.innerWidth < br.md) {
					setColWidth(40 / 48);
					setMarginTop(0.7);
					setBlockHeight(6);
				} else if (window.innerWidth < br.ml) {
					setColWidth(34 / 48);
					setMarginTop(0.5);
					setBlockHeight(5);
				} else if (window.innerWidth < br.mx) {
					setColWidth(24 / 48);
					setMarginTop(0.5);
					setBlockHeight(3);
				} else {
					setColWidth(20 / 48);
					setMarginTop(0.5);
					setBlockHeight(3);
				}
			};

			updateWindowHeight();
			updateWindowWidth();
			window.addEventListener("resize", handleResize);

			return () => {
				window.removeEventListener("resize", handleResize);
			};
		}, []);

		return (
			<Visualisation
				ref={ref}
				style={{ height: `calc(${height * 100}vw + ${marginTop}vh)` }}>
				<Screen>
					<ScrollContainer
						style={{
							x: progressX,
							y: progressY,
						}}>
						{data
							?.slice()
							.reverse()
							.map((item, i) => (
								<Section key={i}>
									<Content style={{ minHeight: `${marginTop * 100}vh` }}>
										<Number>
											CLASS {data.length - i} <span>of</span> {data.length}
										</Number>
										<Title>{item.title}</Title>
										<RichText dangerouslySetInnerHTML={{ __html: item.text }} />
									</Content>
									<Block height={`${item.value * 100}vw`}>
										{i === 0 ? (
											<Ground>
												<StaticImage
													css={povertyLine3Image}
													src="../../../images/poverty-line3.png"
													alt="Line of people in poverty"
													placeholder="none"
													layout="fullWidth"
												/>
												<StaticImage
													css={povertyLine2Image}
													src="../../../images/poverty-line2.png"
													alt="Line of people in poverty"
													placeholder="none"
													layout="fullWidth"
												/>
												<StaticImage
													css={povertyLine1Image}
													src="../../../images/poverty-line1.png"
													alt="Line of people in poverty"
													placeholder="none"
													layout="fullWidth"
												/>
												<StaticImage
													css={povertyManImage}
													src="../../../images/poverty-character.png"
													alt="Man looking up at next social class"
													placeholder="none"
													layout="fullWidth"
													sizes="(min-width: 1024px) 42vw, (min-width: 768px) 71vw, 84vw"
												/>
											</Ground>
										) : (
											<>
												<PillarTop>
													{item.imageEl}
													<StaticImage
														css={pillarTopImage}
														src="../../../images/pillar-top.png"
														alt="Top of pillar"
														placeholder="none"
														layout="fullWidth"
														aspectRatio={2184 / 1050}
														sizes="(min-width: 1024px) 42vw, (min-width: 768px) 71vw, 84vw"
														loading="eager"
													/>
												</PillarTop>
												<PillarFront />
												<PillarBottom>
													<StaticImage
														src="../../../images/pillar-bottom.png"
														alt="Bottom of pillar"
														placeholder="none"
														layout="fullWidth"
														aspectRatio={3516 / 1515}
														sizes="(min-width: 1024px) 84vw, (min-width: 768px) 140vw, 160vw"
														loading="eager"
													/>
												</PillarBottom>
											</>
										)}
									</Block>
								</Section>
							))}
					</ScrollContainer>
				</Screen>
			</Visualisation>
		);
	})
);

export default function SocialWrapper({ classes }) {
	const area = useGetResizer();
	const ref = useRef();
	const [refTop, setRefTop] = useState(0);
	const [refBottom, setRefBottom] = useState(1);
	useEffect(() => {
		const refPosition = ref.current?.getBoundingClientRect();
		const top = refPosition.top + window.pageYOffset;
		const bottom = top + refPosition.height - window.innerHeight;
		setRefTop(top);
		setRefBottom(bottom);
	}, [area]);
	return (
		<SocialClasses
			ref={ref}
			refTop={refTop}
			refBottom={refBottom}
			classes={classes}
		/>
	);
}
